import React from 'react';
import { PanelId, PANELS, PANEL_HEIGHT } from '../panels/registry';

// The shell every panel in the row sits in.
//
// It owns two things and nothing else:
//   1. the panel's width and height, taken from the registry so the row and the
//      panel can never disagree about how much space it takes;
//   2. the shared chrome — translucent ground, hairline border, drop shadow —
//      which used to be repeated inline on each panel.
//
// Panels that draw their own surround (the Chord Compass has a gradient and an
// inset shadow; the Staff draws its own paper) pass chrome={false} and keep
// their existing class.
//
// Position is handed in as `x`, worked out from the stack (stackPositions in
// panels/registry), so a panel's place in the JSX does not have to match its
// place on screen. At rest a panel is placed by `left` and carries no
// transform: a transform would make every panel its own stacking context and
// trap the z-indexes of things that hang out of it. The stack's slides are
// transforms only while they run.

/** Ground colour behind a panel. Kept as a lookup rather than a ternary chain. */
export function panelBackground(appTheme: string, lightAlpha: number): string {
  if (appTheme === 'dark') {
    return 'rgba(0, 0, 0, 0.6)';
  }
  if (appTheme === 'light') {
    return `rgba(255, 255, 255, ${lightAlpha})`;
  }
  return 'rgba(0, 0, 0, 0.8)';
}

export function panelShadowAlpha(appTheme: string): number {
  if (appTheme === 'light') {
    return 0.2;
  }
  return 0.5;
}

/**
 * Holds its children's last render while `frozen`. A panel sliding out of view
 * is still on screen for its fade, but it is no longer live: it keeps the
 * frame it left with instead of re-rendering as the music changes.
 */
const Frozen = React.memo(
  ({ children }: { frozen: boolean; children?: React.ReactNode }) => <>{children}</>,
  (_prev, next) => next.frozen
);

interface PanelProps {
  id: PanelId;
  appTheme: string;
  /** Left edge in dvw from the row's left edge. */
  x: number;
  /** Leaving the view: drawn, but not live and not clickable. */
  frozen?: boolean;
  /** Swap with the panel on that side. Omitted for a panel that cannot move. */
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
  onHide?: () => void;
  /** False at the ends of the stack, where that arrow is not drawn. */
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
  /**
   * The padlock, drawn in the left arrow's place, or just right of the left
   * arrow when the panel can still move left. 'unlocked' offers to lock the
   * panel on the left, 'locked' to unlock it. Omitted where neither applies.
   */
  lock?: 'unlocked' | 'locked';
  onToggleLock?: () => void;
  /** Draw the shared surround. Off for panels that bring their own. */
  chrome?: boolean;
  /** Ground opacity in the light theme — panels differ slightly. */
  lightAlpha?: number;
  padding?: string;
  className?: string;
  /** Escape hatch for per-panel styles that are not layout. */
  style?: React.CSSProperties;
  /** DOM id, where something outside still looks the element up. */
  domId?: string;
  /**
   * Wraps the children in an inner box carrying this class. A panel that
   * scrolls needs one: putting overflow on the panel itself would clip its own
   * tab, which hangs below the panel and is positioned against it.
   */
  bodyClassName?: string;
  /** The scrolling body, for a panel that has to measure or watch its scroll. */
  bodyRef?: React.RefObject<HTMLDivElement>;
  onBodyScroll?: React.UIEventHandler<HTMLDivElement>;
  /**
   * Clicks anywhere in the scrolling body, including its blank space. The
   * chart uses it to drop the chord selection when you click its paper.
   */
  onBodyClick?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Drawn over the panel but OUTSIDE the scrolling body, so it holds still
   * while the body scrolls under it. Anything anchored to the panel's edges —
   * the chart's bottom fade, say — belongs here rather than in `children`.
   */
  overlay?: React.ReactNode;
  children?: React.ReactNode;
}

const Panel: React.FC<PanelProps> = ({
  id,
  appTheme,
  x,
  frozen,
  onMoveLeft,
  onMoveRight,
  onHide,
  canMoveLeft,
  canMoveRight,
  lock,
  onToggleLock,
  chrome,
  lightAlpha,
  padding,
  className,
  style,
  domId,
  bodyClassName,
  bodyRef,
  onBodyScroll,
  onBodyClick,
  overlay,
  children,
}) => {
  const def = PANELS[id];
  const alpha = lightAlpha === undefined ? 0.7 : lightAlpha;

  // margin is pinned to 0 because the stack's positions own the spacing — a
  // legacy class margin leaking back in would silently shift the panel.
  const inset = def.topInset === undefined ? 0 : def.topInset;
  const layout: React.CSSProperties = {
    width: `${def.width}dvw`,
    height: `${PANEL_HEIGHT - inset}dvw`,
    position: 'absolute',
    left: `${x}dvw`,
    top: 0,
    margin: 0,
    marginTop: `${inset}dvw`,
    pointerEvents: frozen ? 'none' : undefined,
  };

  const surround: React.CSSProperties = chrome
    ? {
      backgroundColor: panelBackground(appTheme, alpha),
      padding: padding === undefined ? '0.5dvw' : padding,
      borderStyle: 'solid',
      borderColor: '#555',
      borderRadius: '1dvw',
      borderWidth: '0.05dvw',
      boxShadow: `0 0 0.6dvw 0.35dvw rgba(0, 0, 0, ${panelShadowAlpha(appTheme)})`,
    }
    : {};

  const merged = Object.assign({}, layout, surround, style);

  // Icons are drawn dark on the light theme and light on the other two.
  const light = appTheme === 'light';
  const iconTone = light ? 'black' : 'white';
  // The tab is made of the same material as a panel — same translucent ground,
  // same #555 hairline, same shadow — so it reads as part of the panel it hangs
  // from rather than as a chip floating over the piano.
  const notchStyle: React.CSSProperties = {
    background: panelBackground(appTheme, 0.7),
    borderColor: '#555',
    // Offset downward with a negative spread so the shadow falls below the tab
    // instead of haloing up onto the panel it hangs from.
    boxShadow: `0 0.4dvw 0.45dvw -0.2dvw rgba(0, 0, 0, ${panelShadowAlpha(appTheme)})`,
  };
  const gripStyle: React.CSSProperties = {
    background: light ? 'rgba(40, 40, 40, 0.4)' : 'rgba(255, 255, 255, 0.36)',
  };
  const hideStyle: React.CSSProperties = {
    color: light ? 'rgb(25, 25, 25)' : 'rgb(238, 238, 238)',
  };
  // The padlocks come in white only, so the light theme inverts them to match
  // the black arrows beside them.
  const padlockStyle: React.CSSProperties = light ? { filter: 'invert(1)' } : {};

  const hasControls = onMoveLeft !== undefined || onMoveRight !== undefined || onHide !== undefined;
  // The padlock takes the left arrow's slot only where the arrow would be
  // hidden anyway. The leftmost panel of the view can still have panels
  // scrolled off to its left, and then it carries both, in a wider tab.
  const showMoveLeft = onMoveLeft !== undefined && (lock === undefined || canMoveLeft === true);
  const wideNotch = lock !== undefined && showMoveLeft;

  return (
    <div id={domId} className={className} style={merged} data-panel={id}>
      {bodyClassName === undefined
        ? <Frozen frozen={frozen === true}>{children}</Frozen>
        : (
          <div className={bodyClassName} ref={bodyRef} onScroll={onBodyScroll} onClick={onBodyClick}>
            <Frozen frozen={frozen === true}>{children}</Frozen>
          </div>
        )}
      <Frozen frozen={frozen === true}>{overlay}</Frozen>
      {/* A tab hanging from the panel's bottom edge. Every control lives out
          here, outside the panel's box, so none of them can sit over a chord,
          a button or anything else the panel draws. Hover is scoped to the tab
          rather than to the whole panel for the same reason.
          The tab is already wide enough to hold all three controls at rest, so
          hovering reveals them in place instead of making anything appear
          outside it. An arrow that cannot move — the first panel in the stack
          has nowhere to go left, the last nowhere right, and a locked panel
          nowhere at all — stays in the layout but is hidden, which keeps the
          close button centred. The leftmost panel of the view, and the
          innermost locked one, carry a padlock in the left arrow's place —
          unless the panel can still move left, when the tab widens to hold
          the arrow and the padlock both, and the four controls are centred
          as a group instead. */}
      {hasControls && (
        // data-actionable: rearranging panels is not a click away from the
        // chart, so it keeps the chart's chord selection (handleGlobalClick).
        <div className={wideNotch ? 'panel-notch panel-notch-wide' : 'panel-notch'} style={notchStyle} data-actionable="true">
          <span className="panel-grip" aria-hidden="true">
            <span className="panel-dot" style={gripStyle} />
            <span className="panel-dot" style={gripStyle} />
            <span className="panel-dot" style={gripStyle} />
          </span>
          {showMoveLeft && (
            <button
              type="button"
              className="panel-control"
              onClick={onMoveLeft}
              title={`Move ${def.title} left`}
              style={{ visibility: canMoveLeft ? 'visible' : 'hidden' }}
            >
              <img src={`/images/Icons/move-panel-left_${iconTone}.png`} alt={`Move ${def.title} left`} />
            </button>
          )}
          {lock !== undefined && (
            <button
              type="button"
              className="panel-control"
              onClick={onToggleLock}
              title={lock === 'locked' ? `Unlock ${def.title}` : `Lock ${def.title} on the left`}
            >
              <img
                src={`/images/Icons/padlock-${lock}.png`}
                alt={lock === 'locked' ? `Unlock ${def.title}` : `Lock ${def.title}`}
                style={padlockStyle}
              />
            </button>
          )}
          {onHide !== undefined && (
            <button
              type="button"
              className="panel-control panel-hide"
              onClick={onHide}
              title={`Hide ${def.title}`}
              style={hideStyle}
            >
              {'✕'}
            </button>
          )}
          {onMoveRight !== undefined && (
            <button
              type="button"
              className="panel-control"
              onClick={onMoveRight}
              title={`Move ${def.title} right`}
              style={{ visibility: canMoveRight ? 'visible' : 'hidden' }}
            >
              <img src={`/images/Icons/move-panel-right_${iconTone}.png`} alt={`Move ${def.title} right`} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Panel;
